import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { NameListManager } from "@/inventory/components/name-list-manager"
import {
  createSupplyCategoryFn,
  createSupplyVendorFn,
  ensureDefaultCategoriesFn,
  listSupplyCategoriesFn,
  listSupplyVendorsFn,
  updateSupplyCategoryFn,
  updateSupplyVendorFn,
} from "@/supplies/rpc"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/card"

export const Route = createFileRoute("/_supplies-layout/supplies/settings")({
  component: SettingsPage,
})

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message
  }
  return "Something went wrong"
}

function SettingsPage() {
  const queryClient = useQueryClient()
  const [categoryError, setCategoryError] = useState<string | null>(null)
  const [vendorError, setVendorError] = useState<string | null>(null)

  const seedQuery = useQuery({
    queryKey: ["supply-categories", "ensure-defaults"],
    queryFn: () => ensureDefaultCategoriesFn(),
  })

  const categoriesQuery = useQuery({
    queryKey: ["supply-categories", { includeInactive: true }],
    queryFn: () => listSupplyCategoriesFn({ data: { includeInactive: true } }),
    enabled: seedQuery.isSuccess,
  })

  const vendorsQuery = useQuery({
    queryKey: ["supply-vendors", { includeInactive: true }],
    queryFn: () => listSupplyVendorsFn({ data: { includeInactive: true } }),
  })

  const createCategory = useMutation({
    mutationFn: (name: string) => createSupplyCategoryFn({ data: { name } }),
    onSuccess: async () => {
      setCategoryError(null)
      await queryClient.invalidateQueries({ queryKey: ["supply-categories"] })
    },
    onError: (error) => setCategoryError(getErrorMessage(error)),
  })

  const updateCategory = useMutation({
    mutationFn: (input: { id: number; isActive: boolean }) =>
      updateSupplyCategoryFn({ data: input }),
    onSuccess: async () => {
      setCategoryError(null)
      await queryClient.invalidateQueries({ queryKey: ["supply-categories"] })
    },
    onError: (error) => setCategoryError(getErrorMessage(error)),
  })

  const createVendor = useMutation({
    mutationFn: (name: string) => createSupplyVendorFn({ data: { name } }),
    onSuccess: async () => {
      setVendorError(null)
      await queryClient.invalidateQueries({ queryKey: ["supply-vendors"] })
    },
    onError: (error) => setVendorError(getErrorMessage(error)),
  })

  const updateVendor = useMutation({
    mutationFn: (input: { id: number; isActive: boolean }) =>
      updateSupplyVendorFn({ data: input }),
    onSuccess: async () => {
      setVendorError(null)
      await queryClient.invalidateQueries({ queryKey: ["supply-vendors"] })
    },
    onError: (error) => setVendorError(getErrorMessage(error)),
  })

  const categoriesLoading =
    seedQuery.isLoading || categoriesQuery.isLoading || seedQuery.isFetching
  const seedError = seedQuery.isError ? getErrorMessage(seedQuery.error) : null

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-semibold text-2xl tracking-tight">Settings</h1>
        <p className="mt-1 text-muted-foreground text-sm">
          Manage categories and vendors used across supplies.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Categories</CardTitle>
            <CardDescription>
              Groupings for supply items (Office and Store are seeded by
              default).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <NameListManager
              title="Category name"
              placeholder="e.g. Packaging"
              emptyMessage="No categories yet."
              items={categoriesQuery.data ?? []}
              isLoading={categoriesLoading}
              isAdding={createCategory.isPending}
              isTogglingId={
                updateCategory.isPending
                  ? (updateCategory.variables?.id ?? null)
                  : null
              }
              error={
                categoryError ??
                seedError ??
                (categoriesQuery.isError
                  ? getErrorMessage(categoriesQuery.error)
                  : null)
              }
              onAdd={(name) => createCategory.mutateAsync(name)}
              onToggleActive={(id, isActive) =>
                updateCategory.mutateAsync({ id, isActive })
              }
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Vendors</CardTitle>
            <CardDescription>
              Suppliers used when receiving supplies.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <NameListManager
              title="Vendor name"
              placeholder="e.g. Office Depot"
              emptyMessage="No vendors yet."
              items={vendorsQuery.data ?? []}
              isLoading={vendorsQuery.isLoading}
              isAdding={createVendor.isPending}
              isTogglingId={
                updateVendor.isPending
                  ? (updateVendor.variables?.id ?? null)
                  : null
              }
              error={
                vendorError ??
                (vendorsQuery.isError
                  ? getErrorMessage(vendorsQuery.error)
                  : null)
              }
              onAdd={(name) => createVendor.mutateAsync(name)}
              onToggleActive={(id, isActive) =>
                updateVendor.mutateAsync({ id, isActive })
              }
            />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
